import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function EnergyUtilitiesExample() {
  useSEO({
    title: 'Energy & Utilities Simulation',
    description: 'Simulate load patterns, renewable intermittency, and grid capacity. Balance the grid and prevent outages.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Grid + Demand Management</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Balance the grid and prevent outages</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate load patterns, renewable intermittency, and infrastructure capacity to keep the lights on reliably.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Grid Operators</p><p className="text-sm text-gray-600">Balancing supply and demand in real-time</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Utility Planners</p><p className="text-sm text-gray-600">Forecasting capacity needs and investments</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Energy Traders</p><p className="text-sm text-gray-600">Optimizing power purchase decisions</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How much reserve capacity to maintain</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to dispatch different generation sources</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How renewable variability affects grid stability</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Where to invest in infrastructure upgrades</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How demand response programs affect peak loads</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between reliability and cost</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Regional Utility</p>
            <p className="text-lg leading-relaxed mb-6">A regional utility simulated solar generation variability, peak demand periods, and battery storage dispatch strategies. The model showed that strategic battery deployment at three substations—combined with adjusted demand response triggers—would reduce blackout risk by 60% while cutting peak power purchase costs by 22%.</p>
            <p className="text-gray-400 text-sm">The impact: $4.5M annual savings with improved grid reliability metrics.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your grid dynamics, test dispatch scenarios, and share insights with planning teams. Ideal for utilities who want ongoing analytical capability."
          consultingDescription="Partner with our energy consultants to build a custom grid model, run scenario workshops, and deliver regulatory-ready recommendations."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/mining-manufacturing" className="text-gray-700 hover:text-gray-900 text-sm">Asset Uptime + Maintenance →</Link>
            <Link to="/solutions/public-policy" className="text-gray-700 hover:text-gray-900 text-sm">Policy Impact Modeling →</Link>
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default EnergyUtilitiesExample;
